import { useEffect, useState } from 'react'
import { useParams, Link, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { listCreatorEpisodesForSeries, listCreatorSeries, type CreatorSeriesSummary, type CreatorEpisodeSummary } from '@/lib/api/creator'

export function CreatorSeriesDetail() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const [series, setSeries] = useState<CreatorSeriesSummary | null>(null)
  const [episodes, setEpisodes] = useState<CreatorEpisodeSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    let cancelled = false
    setLoading(true)
    setError(null)

    Promise.all([listCreatorSeries(), listCreatorEpisodesForSeries(id)])
      .then(([allSeries, eps]) => {
        if (cancelled) return
        const found = allSeries.find((s) => s.id === id) ?? null
        setSeries(found)
        setEpisodes(eps)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load series')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [id])

  const fromDashboard = searchParams.get('from') === 'dashboard'

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="mb-1 text-xs text-gray-500">
            <Link to="/creator/series" className="hover:text-[#D4AF37]">
              Series
            </Link>
            <span className="mx-1">/</span>
            <span className="text-gray-400">
              {series?.title ?? (loading ? 'Loading…' : 'Unknown series')}
            </span>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {series?.title ?? 'Series detail'}
          </h1>
          {series?.synopsis && (
            <p className="mt-1 text-sm text-gray-400 line-clamp-2">{series.synopsis}</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            className="border-gray-800 text-gray-300 hover:bg-white/5"
            asChild
          >
            <Link to={`/creator/upload?seriesId=${encodeURIComponent(id ?? '')}`}>
              Upload episode
            </Link>
          </Button>
          {fromDashboard && (
            <Button
              variant="secondary"
              className="border-gray-800 text-gray-300 hover:bg-white/5"
              asChild
            >
              <Link to="/creator">Back to overview</Link>
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-700 bg-red-900/30 px-3 py-2 text-xs text-red-200">
          {error}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1.6fr)]">
        <div className="rounded-xl border border-gray-800 bg-black/40 p-4 space-y-2 text-sm text-gray-300">
          <p>
            <span className="text-xs uppercase tracking-wide text-gray-500">Genre: </span>
            {series?.genre || '—'}
          </p>
          <p>
            <span className="text-xs uppercase tracking-wide text-gray-500">Language: </span>
            {series?.language || '—'}
          </p>
          <p>
            <span className="text-xs uppercase tracking-wide text-gray-500">Episodes: </span>
            {series?.episodeCount ?? episodes.length}
          </p>
          {series?.status && (
            <p>
              <span className="text-xs uppercase tracking-wide text-gray-500">Status: </span>
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${
                  series.status === 'approved'
                    ? 'bg-emerald-900/30 text-emerald-300 border border-emerald-700'
                    : series.status === 'pending_review'
                    ? 'bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/60'
                    : series.status === 'rejected'
                    ? 'bg-red-900/30 text-red-300 border border-red-700'
                    : 'bg-yellow-900/30 text-yellow-200 border border-yellow-700'
                }`}
              >
                {series.status === 'approved'
                  ? 'Published'
                  : series.status === 'pending_review'
                  ? 'Pending review'
                  : series.status === 'rejected'
                  ? 'Rejected'
                  : 'Draft'}
              </span>
            </p>
          )}
        </div>

        <div className="rounded-xl border border-gray-800 bg-black/40 p-4">
          <p className="text-xs text-gray-400">
            Editing series metadata and scheduling premieres will be added here once the backend
            supports those operations from the web studio.
          </p>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-800 bg-black/40">
        <div className="grid grid-cols-[minmax(0,2.1fr)_minmax(0,0.8fr)_minmax(0,0.8fr)_minmax(0,1.2fr)] gap-3 px-4 py-2 text-xs font-medium uppercase tracking-wide text-gray-500 border-b border-gray-800">
          <div>Episode</div>
          <div>No.</div>
          <div>Duration</div>
          <div>Status</div>
        </div>
        {loading ? (
          <div className="px-4 py-6 text-sm text-gray-400">Loading episodes…</div>
        ) : episodes.length === 0 ? (
          <div className="px-4 py-6 text-sm text-gray-400">
            No episodes yet. Use &quot;Upload episode&quot; to add the first one.
          </div>
        ) : (
          <div className="divide-y divide-gray-900">
            {episodes.map((ep) => (
              <div
                key={ep.id}
                className="grid grid-cols-[minmax(0,2.1fr)_minmax(0,0.8fr)_minmax(0,0.8fr)_minmax(0,1.2fr)] gap-3 px-4 py-3 text-sm items-center"
              >
                <div>
                  <p className="font-medium text-white line-clamp-1">{ep.title}</p>
                  <p className="text-xs text-gray-500">
                    {ep.releasedAt ? `Released ${new Date(ep.releasedAt).toLocaleDateString()}` : 'Not yet released'}
                  </p>
                </div>
                <div className="text-sm text-gray-300">{ep.episodeNumber ?? '—'}</div>
                <div className="text-sm text-gray-300">
                  {ep.duration ? `${Math.round(ep.duration / 60)} min` : '—'}
                </div>
                <div className="text-xs capitalize">
                  <span className="inline-flex items-center rounded-full px-2 py-0.5 bg-gray-900/40 text-gray-300 border border-gray-700">
                    {ep.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

